/*
À propos screen — credits + third-party attributions.

Mirrors the audit's NOTICE.md draft. Every embedded asset and library
that requires attribution under its license is listed here with the
license short name and a link to the upstream project. Adding a new
dependency that requires attribution? Add it here.
*/

import { mountInfoScreen, type InfoScreenContent } from './components/InfoScreen'

const kContent: InfoScreenContent = {
	title: 'À propos',
	leadHtml: `
		CanoPaname est une application personnelle pour découvrir les arbres
		parisiens — sans serveur, sans compte, sans traqueur. Cette page
		liste les sources de données, polices et bibliothèques sur
		lesquelles l'app s'appuie, avec leurs licences.
		<br /><span class="info-note">Version build ${__APP_BUILD_COUNT__}</span>
	`,
	sections: [
		{
			heading: 'Données',
			bodyHtml: `
				<ul class="info-list">
					<li>
						<strong>Arbres de Paris</strong> — datasets
						<a class="info-link" href="https://opendata.paris.fr/explore/dataset/les-arbres/" target="_blank" rel="noopener">les-arbres</a>
						et
						<a class="info-link" href="https://opendata.paris.fr/explore/dataset/arbresremarquablesparis/" target="_blank" rel="noopener">arbresremarquablesparis</a>,
						© Ville de Paris, sous
						<a class="info-link" href="https://opendatacommons.org/licenses/odbl/1-0/" target="_blank" rel="noopener">ODbL 1.0</a>.
						Le bundle embarqué dans l'app est une base de données dérivée
						au sens de l'ODbL §4.4 et reste sous ODbL.
					</li>
					<li>
						<strong>Fiches essences</strong> — «&nbsp;Guide des essences de
						Paris&nbsp;» — Ville de Paris via
						<a class="info-link" href="https://opendata.paris.fr" target="_blank" rel="noopener">OpenData Paris</a>,
						sous
						<a class="info-link" href="https://opendatacommons.org/licenses/odbl/1-0/" target="_blank" rel="noopener">ODbL v1.0</a>.
					</li>
					<li>
						<strong>Résumés d'espèces</strong> — extraits de
						<a class="info-link" href="https://fr.wikipedia.org" target="_blank" rel="noopener">Wikipédia FR</a>
						via l'API REST «&nbsp;summary&nbsp;», contributeurs Wikipédia, sous
						<a class="info-link" href="https://creativecommons.org/licenses/by-sa/4.0/" target="_blank" rel="noopener">CC BY-SA 4.0</a>.
					</li>
				</ul>
				<p class="info-note">
					CanoPaname est un projet indépendant et n'est pas affilié à la
					Ville de Paris.
				</p>
			`,
		},
		{
			heading: '🗺️ Cartographie',
			bodyHtml: `
				<ul class="info-list">
					<li>
						<strong>Tuiles &amp; style</strong> —
						<a class="info-link" href="https://openfreemap.org" target="_blank" rel="noopener">OpenFreeMap</a>
						(style Liberty), basé sur les données
						<a class="info-link" href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>
						© contributeurs OSM, sous
						<a class="info-link" href="https://opendatacommons.org/licenses/odbl/1-0/" target="_blank" rel="noopener">ODbL 1.0</a>.
					</li>
				</ul>
			`,
		},
		{
			heading: '✒️ Police',
			bodyHtml: `
				<ul class="info-list">
					<li>
						<strong>Fraunces SemiBold</strong> — Phaedra Charles &amp;
						Flavia Zimbardi (Undercase Type),
						<a class="info-link" href="https://github.com/undercasetype/Fraunces" target="_blank" rel="noopener">github.com/undercasetype/Fraunces</a>,
						sous
						<a class="info-link" href="https://scripts.sil.org/OFL" target="_blank" rel="noopener">SIL Open Font License 1.1</a>.
					</li>
				</ul>
			`,
		},
		{
			heading: '📚 Bibliothèques',
			bodyHtml: `
				<ul class="info-list">
					<li>
						<strong>MapLibre GL JS</strong> —
						<a class="info-link" href="https://maplibre.org" target="_blank" rel="noopener">maplibre.org</a>,
						sous
						<a class="info-link" href="https://github.com/maplibre/maplibre-gl-js/blob/main/LICENSE.txt" target="_blank" rel="noopener">licence ISC</a>
						(© MapLibre contributors). Inclut du code BSD-3
						hérité de Mapbox GL JS pré-fork.
					</li>
					<li>
						<strong>fflate</strong> —
						<a class="info-link" href="https://github.com/101arrowz/fflate" target="_blank" rel="noopener">github.com/101arrowz/fflate</a>,
						sous
						<a class="info-link" href="https://github.com/101arrowz/fflate/blob/master/LICENSE" target="_blank" rel="noopener">licence MIT</a>
						(© Arjun Barrett).
					</li>
					<li>
						<strong>TypeScript &amp; Vite</strong> — Microsoft &amp; Evan You,
						sous
						<a class="info-link" href="https://opensource.org/licenses/MIT" target="_blank" rel="noopener">licence MIT</a>
						(outils de build, non distribués au runtime).
					</li>
				</ul>
			`,
		},
		{
			heading: '📷 Crédits photos',
			bodyHtml: `
				<p>
					Photos de référence des espèces, par source.
					<a class="info-link" href="#/photo-credits">Voir les crédits photos</a>.
				</p>
			`,
		},
		{
			heading: '⚖️ Licence de l\'app',
			bodyHtml: `
				<p>
					Le code source de CanoPaname est distribué sous
					<a class="info-link" href="https://opensource.org/licenses/MIT" target="_blank" rel="noopener">licence MIT</a>.
					Repo public sur
					<a class="info-link" href="https://github.com/m4xim1nus/CanoPaname" target="_blank" rel="noopener">GitHub</a>.
				</p>
			`,
		},
	],
	footerHtml: `
		<p class="info-footnote">
			Une attribution oubliée&nbsp;? Ouvre une issue sur le repo GitHub —
			merci de signaler.
		</p>
	`,
}

export function mountAboutScreen(inRoot: HTMLElement): void
{
	mountInfoScreen(inRoot, kContent)
}
